declare function require(name: string): any;
const pako = require("pako");
import {UAParser} from "ua-parser-js";

class API {
    static transportMethod = "HTTP";
    static useSameEndpointForHttp = false;
    private static _deviceDataSent = false;
    private static _connecting = false;
    private static _socket: WebSocket;
    private static _lastSocketError: Event;
    private static _waitingConnection: {resolve: () => void, reject: (error: Error) => void}[] = [];
    private static _returnTable: {resolve: (data: any) => void, reject: (error: Error) => void}[] = [];
    private static _callbackTable: ((args: any) => void)[] = [];

    private static _makeHttpRequest(func: string, body: string) {
        return new Promise<any>((resolve, reject) => {
            const req = new XMLHttpRequest();
            let httpURL = "https://api.meumedico.net.br/http";
            req.open("POST", httpURL + "/" + func);
            const deviceId = localStorage.getItem("deviceId");
            if (deviceId && deviceId !== "undefined")
                req.setRequestHeader("x-device-id", deviceId);
            req.onreadystatechange = () => {
                if (req.readyState !== 4) return;
                if (req.status === 200)
                    resolve(req.responseText);
                else {
                    const error = JSON.parse(req.responseText);
                    reject(error);
                }
            };
            req.send(body);
        });
    }

    static async _connect() {
        const parser = new UAParser();
        parser.setUA(navigator.userAgent);
        const extraData = parser.getResult();
        const deviceData: any = {};
        deviceData["type"] = "browser";
        deviceData["screen_width"] = screen.width;
        deviceData["screen_height"] = screen.height;
        deviceData["language"] = navigator.language;
        deviceData["os"] = extraData.os.name;
        deviceData["os_version"] = extraData.os.version;
        deviceData["browser"] = extraData.browser.name;
        deviceData["browser_version"] = extraData.browser.version;
        deviceData["browser_device"] = extraData.device.type || "desktop";
        deviceData["referrer"] = document.referrer;

        if (API.transportMethod === "HTTP") {
            const deviceId = localStorage.getItem("deviceId");
            if (!(deviceId && deviceId !== "undefined") || !API._deviceDataSent) {
                await new Promise(async (resolve, reject) => {
                    API._waitingConnection.push({resolve, reject});
                    if (!API._connecting) {
                        API._connecting = true;
                        try {
                            const deviceId = await API._makeHttpRequest("_connect", JSON.stringify(deviceData));
                            localStorage.setItem("deviceId", deviceId);
                            API._deviceDataSent = true;
                            API._waitingConnection.forEach(obj => obj.resolve());
                            API._waitingConnection = [];
                        } catch (e) {
                            API._waitingConnection.forEach(obj => obj.reject(e));
                            API._waitingConnection = [];
                        } finally {
                            API._connecting = false;
                        }
                    }
                });
            }
        } else if (API.transportMethod === "WS") {
            if (API._socket && API._socket.readyState === WebSocket.OPEN) return;
            await new Promise((resolve, reject) => {
                API._waitingConnection.push({resolve, reject});
                if (!API._connecting) {
                    API._connecting = true;
                    let wsURL = "wss://api.meumedico.net.br";
                    API._socket = new WebSocket(wsURL + "/");
                    API._socket.onopen = () => {
                        if (localStorage.getItem("deviceId"))
                            deviceData["deviceId"] = localStorage.getItem("deviceId");
                        API._socket.send(pako.deflateRaw(JSON.stringify(deviceData)));
                        API._waitingConnection.forEach(obj => obj.resolve());
                        API._waitingConnection = [];
                        API._connecting = false;
                    };
                    API._socket.onerror = err => {
                        console.error(err);
                        if (API._connecting) {
                            API._socket = null;
                            API._connecting = false;
                            API._waitingConnection.forEach(obj => obj.reject(new Error("Conexão com o servidor falhou")));
                            API._waitingConnection = [];
                        } else {
                            API._lastSocketError = err;
                        }
                    };
                    API._socket.onmessage = msg => {
                        const reader = new FileReader();
                        reader.addEventListener("loadend", () => {
                            const data = JSON.parse(pako.inflateRaw(reader.result, {to: "string"}) as any);
                            switch (data["type"]) {
                                case "function-return": {
                                    const id = data["returnId"];
                                    const ret = API._returnTable[id];
                                    API._returnTable[id] = null;
                                    if (data["error"]) {
                                        ret.reject(data["error"]);
                                    } else {
                                        ret.resolve(data["value"]);
                                    }
                                    break;
                                }
                                case "save-device-id": {
                                    localStorage.setItem("deviceId", data["deviceId"]);
                                }
                                case "callback-call": {
                                    const id = data["callbackId"];
                                    const func = API._callbackTable[id];
                                    if (func) {
                                        func(data["args"]);
                                    }
                                    break;
                                }
                            }
                        });
                        reader.readAsBinaryString(msg.data);
                    };
                };
            });
        }
    }

    private static _remoteCall(name: string, data: any) {
        if (API.transportMethod === "HTTP") {
            return (async () => {
                await API._connect();
                return JSON.parse(await API._makeHttpRequest(name, JSON.stringify({args: data})))["value"];
            })();
        } else if (API.transportMethod === "WS") {
            return new Promise<any>((resolve, reject) => {
                API._connect().then(() => {
                    const msg = {type: "function-call", name: name, args: data, returnId: API._returnTable.length};
                    API._returnTable.push({resolve, reject});
                    API._socket.send(pako.deflateRaw(JSON.stringify(msg)));
                }, reject);
            });
        }
    }


    static setGcmToken(token: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.token = "" + token;
            const ret = await API._remoteCall("setGcmToken", args) as void;
            return null;
        })();
    }

    static setPushToken(token: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.token = "" + token;
            const ret = await API._remoteCall("setPushToken", args) as void;
            return null;
        })();
    }

    static sendMail(name: string, email: string, subject: string, message: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.name = "" + name;
            args.email = "" + email;
            args.subject = "" + subject;
            args.message = "" + message;
            const ret = await API._remoteCall("sendMail", args) as void;
            return null;
        })();
    }

    static termosDeUso(): Promise<string> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("termosDeUso", args) as string;
            return "" + ret;
        })();
    }

    static politicaDePrivacidade(): Promise<string> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("politicaDePrivacidade", args) as string;
            return "" + ret;
        })();
    }

    static uploadImage(data: Buffer): Promise<API.ImageUrl> {
        return (async () => {
            const args: any = {};
            args.data = data ? data.toString("base64") : null;
            const ret = await API._remoteCall("uploadImage", args) as API.ImageUrl;
            return ret ? decodeImageUrl(ret) : null;
        })();
    }

    static uploadDocument(name: string, data: Buffer): Promise<API.DocumentUrl> {
        return (async () => {
            const args: any = {};
            args.name = "" + name;
            args.data = data ? data.toString("base64") : null;
            const ret = await API._remoteCall("uploadDocument", args) as API.DocumentUrl;
            return ret ? decodeDocumentUrl(ret) : null;
        })();
    }

    static uploadAudioNote(data: Buffer): Promise<API.AudioNoteUrl> {
        return (async () => {
            const args: any = {};
            args.data = data ? data.toString("base64") : null;
            const ret = await API._remoteCall("uploadAudioNote", args) as API.AudioNoteUrl;
            return ret ? decodeAudioNoteUrl(ret) : null;
        })();
    }

    static doctorTypes(): Promise<API.DoctorType[]> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("doctorTypes", args) as API.DoctorType[];
            return ret ? ret.map((el: API.DoctorType) => el ? decodeDoctorType(el) : null) : null;
        })();
    }

    static requestLoginCode(phone: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.phone = "" + phone;
            const ret = await API._remoteCall("requestLoginCode", args) as void;
            return null;
        })();
    }

    static loginWithCode(phone: string, received_code: string): Promise<API.User> {
        return (async () => {
            const args: any = {};
            args.phone = "" + phone;
            args.received_code = "" + received_code;
            const ret = await API._remoteCall("loginWithCode", args) as API.User;
            return ret ? decodeUser(ret) : null;
        })();
    }

    static currentUser(): Promise<API.User> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("currentUser", args) as API.User;
            return ret ? decodeUser(ret) : null;
        })();
    }

    static logOut(): Promise<void> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("logOut", args) as void;
            return null;
        })();
    }

    static setDetails(details: API.UserDetails): Promise<void> {
        return (async () => {
            const args: any = {};
            args.details = details ? encodeUserDetails(details) : null;
            const ret = await API._remoteCall("setDetails", args) as void;
            return null;
        })();
    }

    static setDoctorDetails(details: API.DoctorDetails): Promise<void> {
        return (async () => {
            const args: any = {};
            args.details = details ? encodeDoctorDetails(details) : null;
            const ret = await API._remoteCall("setDoctorDetails", args) as void;
            return null;
        })();
    }

    static setDoctorDetailsDoc(details: API.SendDoctorDetailsDoc): Promise<API.ImageUrl> {
        return (async () => {
            const args: any = {};
            args.details = details ? encodeSendDoctorDetailsDoc(details) : null;
            const ret = await API._remoteCall("setDoctorDetailsDoc", args) as API.ImageUrl;
            return ret ? decodeImageUrl(ret) : null;
        })();
    }

    static setUserAndDoctorDetails(details: API.UserDetails, doctor_details: API.DoctorDetails): Promise<void> {
        return (async () => {
            const args: any = {};
            args.details = details ? encodeUserDetails(details) : null;
            args.doctor_details = doctor_details ? encodeDoctorDetails(doctor_details) : null;
            const ret = await API._remoteCall("setUserAndDoctorDetails", args) as void;
            return null;
        })();
    }

    static setAvatar(data: Buffer): Promise<API.ImageUrl> {
        return (async () => {
            const args: any = {};
            args.data = data ? data.toString("base64") : null;
            const ret = await API._remoteCall("setAvatar", args) as API.ImageUrl;
            return ret ? decodeImageUrl(ret) : null;
        })();
    }

    static removeAvatar(): Promise<void> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("removeAvatar", args) as void;
            return null;
        })();
    }

    static patientSetup(name: string, email: string, avatar: Buffer): Promise<API.MaybeAvatar> {
        return (async () => {
            const args: any = {};
            args.name = "" + name;
            args.email = "" + email;
            args.avatar = avatar ? avatar.toString("base64") : null;
            const ret = await API._remoteCall("patientSetup", args) as API.MaybeAvatar;
            return ret ? decodeMaybeAvatar(ret) : null;
        })();
    }

    static doctorSetup(name: string, email: string, avatar: Buffer, type: string, doc: string, state: string, doc_photo: Buffer, specialities: string[]): Promise<API.MaybeAvatar> {
        return (async () => {
            const args: any = {};
            args.name = "" + name;
            args.email = "" + email;
            args.avatar = avatar ? avatar.toString("base64") : null;
            args.type = "" + type;
            args.doc = "" + doc;
            args.state = "" + state;
            args.doc_photo = doc_photo ? doc_photo.toString("base64") : null;
            args.specialities = Array.isArray(specialities) ? specialities.map((el: string) => "" + el) : null;
            const ret = await API._remoteCall("doctorSetup", args) as API.MaybeAvatar;
            return ret ? decodeMaybeAvatar(ret) : null;
        })();
    }

    static invitePatient(phone: string): Promise<string> {
        return (async () => {
            const args: any = {};
            args.phone = "" + phone;
            const ret = await API._remoteCall("invitePatient", args) as string;
            return "" + ret;
        })();
    }

    static inviteDoctor(phone: string): Promise<string> {
        return (async () => {
            const args: any = {};
            args.phone = "" + phone;
            const ret = await API._remoteCall("inviteDoctor", args) as string;
            return "" + ret;
        })();
    }

    static getMyContacts(since: Date): Promise<API.User[]> {
        return (async () => {
            const args: any = {};
            args.since = since ? since.toISOString() : null;
            const ret = await API._remoteCall("getMyContacts", args) as API.User[];
            return ret ? ret.map((el: API.User) => el ? decodeUser(el) : null) : null;
        })();
    }

    static getAllMyContacts(): Promise<API.User[]> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("getAllMyContacts", args) as API.User[];
            return ret ? ret.map((el: API.User) => el ? decodeUser(el) : null) : null;
        })();
    }

    static allSpecialities(): Promise<API.Speciality[]> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("allSpecialities", args) as API.Speciality[];
            return ret ? ret.map((el: API.Speciality) => el ? decodeSpeciality(el) : null) : null;
        })();
    }

    static allDoctorFilters(): Promise<API.DoctorFilter[]> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("allDoctorFilters", args) as API.DoctorFilter[];
            return ret ? ret.map((el: API.DoctorFilter) => el ? decodeDoctorFilter(el) : null) : null;
        })();
    }

    static getAllDoctors(query: API.DoctorSearchQuery, skip: number): Promise<API.User[]> {
        return (async () => {
            const args: any = {};
            args.query = query ? encodeDoctorSearchQuery(query) : null;
            args.skip = skip;
            const ret = await API._remoteCall("getAllDoctors", args) as API.User[];
            return ret ? ret.map((el: API.User) => el ? decodeUser(el) : null) : null;
        })();
    }

    static patientHistory(patient_id: string, after: Date): Promise<API.HistoryItem[]> {
        return (async () => {
            const args: any = {};
            args.patient_id = "" + patient_id;
            args.after = after ? after.toISOString() : null;
            const ret = await API._remoteCall("patientHistory", args) as API.HistoryItem[];
            return ret ? ret.map((el: API.HistoryItem) => el ? decodeHistoryItem(el) : null) : null;
        })();
    }

    static patientHistoryWithWrongOrderForDavidDontUseIt(patient_id: string, before: Date): Promise<API.HistoryItem[]> {
        return (async () => {
            const args: any = {};
            args.patient_id = "" + patient_id;
            args.before = before ? before.toISOString() : null;
            const ret = await API._remoteCall("patientHistoryWithWrongOrderForDavidDontUseIt", args) as API.HistoryItem[];
            return ret ? ret.map((el: API.HistoryItem) => el ? decodeHistoryItem(el) : null) : null;
        })();
    }

    static addPatientHistory(patient_id: string, medicine: string, summary: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.patient_id = "" + patient_id;
            args.medicine = "" + medicine;
            args.summary = "" + summary;
            const ret = await API._remoteCall("addPatientHistory", args) as void;
            return null;
        })();
    }

    static listenMessages(since: Date, onchange: (message: API.Message) => void): Promise<string> {
        return (async () => {
            const args: any = {};
            args.since = since ? since.toISOString() : null;
            args.onchange = API._callbackTable.length;
            API._callbackTable.push(data => {
                onchange(data["message"] ? decodeMessage(data["message"]) : null);
            });
            const ret = await API._remoteCall("listenMessages", args) as string;
            return "" + ret;
        })();
    }

    static stopListener(id: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.id = "" + id;
            const ret = await API._remoteCall("stopListener", args) as void;
            return null;
        })();
    }

    static listChats(): Promise<API.ChatDetails[]> {
        return (async () => {
            const args: any = {};
            const ret = await API._remoteCall("listChats", args) as API.ChatDetails[];
            return ret ? ret.map((el: API.ChatDetails) => el ? decodeChatDetails(el) : null) : null;
        })();
    }

    static getMessages(chat_id: string, since: Date, amount: number): Promise<API.Message[]> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            args.since = since ? since.toISOString() : null;
            args.amount = amount;
            const ret = await API._remoteCall("getMessages", args) as API.Message[];
            return ret ? ret.map((el: API.Message) => el ? decodeMessage(el) : null) : null;
        })();
    }

    static sendSpam(target_ids: string[], message: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.target_ids = Array.isArray(target_ids) ? target_ids.map((el: string) => "" + el) : null;
            args.message = "" + message;
            const ret = await API._remoteCall("sendSpam", args) as void;
            return null;
        })();
    }

    static sendMessage(message: API.MessageToSend): Promise<void> {
        return (async () => {
            const args: any = {};
            args.message = message ? encodeMessageToSend(message) : null;
            const ret = await API._remoteCall("sendMessage", args) as void;
            return null;
        })();
    }

    static markMessagesAsRead(chat_id: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            const ret = await API._remoteCall("markMessagesAsRead", args) as void;
            return null;
        })();
    }

    static inviteUserToChat(chat_id: string, profile_id: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            args.profile_id = "" + profile_id;
            const ret = await API._remoteCall("inviteUserToChat", args) as void;
            return null;
        })();
    }

    static renameChat(chat_id: string, name: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            args.name = "" + name;
            const ret = await API._remoteCall("renameChat", args) as void;
            return null;
        })();
    }

    static changeChatAvatar(chat_id: string, data: Buffer): Promise<void> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            args.data = data ? data.toString("base64") : null;
            const ret = await API._remoteCall("changeChatAvatar", args) as void;
            return null;
        })();
    }

    static acceptAndJoinChat(chat_id: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            const ret = await API._remoteCall("acceptAndJoinChat", args) as void;
            return null;
        })();
    }

    static refuseAndDeleteChat(chat_id: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            const ret = await API._remoteCall("refuseAndDeleteChat", args) as void;
            return null;
        })();
    }

    static leftChat(chat_id: string): Promise<void> {
        return (async () => {
            const args: any = {};
            args.chat_id = "" + chat_id;
            const ret = await API._remoteCall("leftChat", args) as void;
            return null;
        })();
    }

    static createGroupChat(users_ids: string[], name: string, avatar: Buffer): Promise<string> {
        return (async () => {
            const args: any = {};
            args.users_ids = Array.isArray(users_ids) ? users_ids.map((el: string) => "" + el) : null;
            args.name = "" + name;
            args.avatar = avatar ? avatar.toString("base64") : null;
            const ret = await API._remoteCall("createGroupChat", args) as string;
            return "" + ret;
        })();
    }

    static createGroupChatWithPatients(users_ids: string[], patient_ids: string[], name: string, avatar: Buffer): Promise<string> {
        return (async () => {
            const args: any = {};
            args.users_ids = Array.isArray(users_ids) ? users_ids.map((el: string) => "" + el) : null;
            args.patient_ids = Array.isArray(patient_ids) ? patient_ids.map((el: string) => "" + el) : null;
            args.name = "" + name;
            args.avatar = avatar ? avatar.toString("base64") : null;
            const ret = await API._remoteCall("createGroupChatWithPatients", args) as string;
            return "" + ret;
        })();
    }

    static inviteToApp(phone: string): Promise<boolean> {
        return (async () => {
            const args: any = {};
            args.phone = "" + phone;
            const ret = await API._remoteCall("inviteToApp", args) as boolean;
            return ret;
        })();
    }

}

namespace API {
    export class EmailAlreadyExists extends Error {
        constructor(msg?: string) {
            super(msg ? "EmailAlreadyExists: " + msg : "EmailAlreadyExists");
        }
    }
    export class WrongLogin extends Error {
        constructor(msg?: string) {
            super(msg ? "WrongLogin: " + msg : "WrongLogin");
        }
    }
    export class NeedsEmailValidation extends Error {
        constructor(msg?: string) {
            super(msg ? "NeedsEmailValidation: " + msg : "NeedsEmailValidation");
        }
    }
    export class NotLoggedIn extends Error {
        constructor(msg?: string) {
            super(msg ? "NotLoggedIn: " + msg : "NotLoggedIn");
        }
    }
    export class InvalidArgument extends Error {
        constructor(msg?: string) {
            super(msg ? "InvalidArgument: " + msg : "InvalidArgument");
        }
    }
    export class LacksPermission extends Error {
        constructor(msg?: string) {
            super(msg ? "LacksPermission: " + msg : "LacksPermission");
        }
    }
    export class NotAvailableAnymore extends Error {
        constructor(msg?: string) {
            super(msg ? "NotAvailableAnymore: " + msg : "NotAvailableAnymore");
        }
    }
    export class PaymentRefused extends Error {
        constructor(msg?: string) {
            super(msg ? "PaymentRefused: " + msg : "PaymentRefused");
        }
    }
    export class DoesntExist extends Error {
        constructor(msg?: string) {
            super(msg ? "DoesntExist: " + msg : "DoesntExist");
        }
    }


    export interface ImageUrl {
        sha256: string;
        small_url: string;
        url: string;
        original_width: number;
        original_height: number;
        nano: Buffer;
    }

    export interface DocumentUrl {
        sha256: string;
        url: string;
        name: string;
        size_bytes: number;
        mime_type: string;
    }

    export interface AudioNoteUrl {
        sha256: string;
        url: string;
        size_bytes: string;
        length_ms: number;
    }

    export interface PushMessageData {
        chat_id: string;
        type: string;
        unread_count?: number;
    }

    export interface PushMessageNotification {
        title: string;
        body: string;
        icon: string;
        color: string;
        badge?: number;
    }

    export interface PushMessage {
        to: string;
        priority: string;
        notification?: API.PushMessageNotification;
        data?: API.PushMessageData;
    }

    export interface Push {
        targets?: string[];
        title: string;
        body: string;
    }

    export interface Speciality {
        id: string;
        name: string;
    }

    export interface UserDetails {
        name: string;
        email?: string;
        avatar?: API.ImageUrl;
        gender: API.Gender;
    }

    export interface DoctorType {
        id: string;
        name: string;
        docName: string;
        canInvitePatient: boolean;
    }

    export interface DoctorDetails {
        type: string;
        typeName: string;
        bio: string;
        doc: string;
        docName: string;
        docPhoto?: API.ImageUrl;
        state: string;
        specialities: API.Speciality[];
        workplaces: string[];
    }

    export interface User {
        name: string;
        email?: string;
        avatar?: API.ImageUrl;
        gender: API.Gender;
        id: string;
        phone: string;
        setup_complete: boolean;
        is_doctor: boolean;
        doctor_details?: API.DoctorDetails;
        is_my_patient: boolean;
        is_my_doctor: boolean;
        updated_at: Date;
    }

    export interface SendDoctorDetailsDoc {
        doc?: string;
        docPhoto?: Buffer;
    }

    export interface MaybeAvatar {
        avatar?: API.ImageUrl;
    }

    export interface DoctorFilter {
        id: string;
        name: string;
    }

    export interface DoctorSearchQuery {
        name?: string;
        speciality?: string;
    }

    export interface HistoryItem {
        id: string;
        time: Date;
        medicine: string;
        summary: string;
    }

    export interface Chat {
        id: string;
        name: string;
        avatar?: API.ImageUrl;
        is_group: boolean;
    }

    export interface BaseMessage {
        id: string;
        updated_at: Date;
        time: Date;
        chat_id: string;
        sender_id: string;
        status: API.MessageStatus;
        type: API.MessageType;
        text?: string;
        image?: API.ImageUrl;
        document?: API.DocumentUrl;
        audio_note?: API.AudioNoteUrl;
    }

    export interface Message {
        id: string;
        updated_at: Date;
        time: Date;
        chat_id: string;
        sender_id: string;
        status: API.MessageStatus;
        type: API.MessageType;
        text?: string;
        image?: API.ImageUrl;
        document?: API.DocumentUrl;
        audio_note?: API.AudioNoteUrl;
        user?: API.User;
        chat?: API.Chat;
    }

    export interface SimpleMessage {
        id: string;
        updated_at: Date;
        time: Date;
        chat_id: string;
        sender_id: string;
        status: API.MessageStatus;
        type: API.MessageType;
        text?: string;
        image?: API.ImageUrl;
        document?: API.DocumentUrl;
        audio_note?: API.AudioNoteUrl;
    }

    export interface MessageToSend {
        id: string;
        chat_id: string;
        type: API.MessageType;
        text?: string;
        image?: Buffer;
        document?: Buffer;
        audio_note?: Buffer;
    }

    export interface ChatDetails {
        id: string;
        name: string;
        avatar?: API.ImageUrl;
        is_group: boolean;
        messages: API.Message[];
        is_member: boolean;
        unread_count: number;
        user?: API.User;
    }


    export enum PushMessageType {
        OpenChat
    }

    export enum Gender {
        Unknown,
        Male,
        Female
    }

    export enum MessageType {
        Text,
        Image,
        Document,
        AudioNote,
        MetaMemberJoined,
        MetaMemberLeft,
        MetaMemberInvited,
        MetaMemberRefusedInvite,
        MetaChatRenamed,
        MetaChatAvatarChanged
    }

    export enum MessageStatus {
        Sending,
        Sent,
        ReadByAll,
        ReadByMe
    }

}

function encodeImageUrl(obj: API.ImageUrl) {
    const encoded: any = {};
    encoded.sha256 = "" + obj.sha256;
    encoded.small_url = "" + obj.small_url;
    encoded.url = "" + obj.url;
    encoded.original_width = obj.original_width;
    encoded.original_height = obj.original_height;
    encoded.nano = obj.nano ? obj.nano.toString("base64") : null;
    return encoded;
}

function encodeDocumentUrl(obj: API.DocumentUrl) {
    const encoded: any = {};
    encoded.sha256 = "" + obj.sha256;
    encoded.url = "" + obj.url;
    encoded.name = "" + obj.name;
    encoded.size_bytes = obj.size_bytes;
    encoded.mime_type = "" + obj.mime_type;
    return encoded;
}

function encodeAudioNoteUrl(obj: API.AudioNoteUrl) {
    const encoded: any = {};
    encoded.sha256 = "" + obj.sha256;
    encoded.url = "" + obj.url;
    encoded.size_bytes = "" + obj.size_bytes;
    encoded.length_ms = obj.length_ms;
    return encoded;
}

function encodePushMessageData(obj: API.PushMessageData) {
    const encoded: any = {};
    encoded.chat_id = "" + obj.chat_id;
    encoded.type = "" + obj.type;
    encoded.unread_count = obj.unread_count === null || obj.unread_count === undefined ? null : obj.unread_count;
    return encoded;
}

function encodePushMessageNotification(obj: API.PushMessageNotification) {
    const encoded: any = {};
    encoded.title = "" + obj.title;
    encoded.body = "" + obj.body;
    encoded.icon = "" + obj.icon;
    encoded.color = "" + obj.color;
    encoded.badge = obj.badge === null || obj.badge === undefined ? null : obj.badge;
    return encoded;
}

function encodePushMessage(obj: API.PushMessage) {
    const encoded: any = {};
    encoded.to = "" + obj.to;
    encoded.priority = "" + obj.priority;
    encoded.notification = obj.notification === null || obj.notification === undefined ? null : obj.notification ? encodePushMessageNotification(obj.notification) : null;
    encoded.data = obj.data === null || obj.data === undefined ? null : obj.data ? encodePushMessageData(obj.data) : null;
    return encoded;
}

function encodePush(obj: API.Push) {
    const encoded: any = {};
    encoded.targets = obj.targets === null || obj.targets === undefined ? null : Array.isArray(obj.targets) ? obj.targets.map((el: string) => "" + el) : null;
    encoded.title = "" + obj.title;
    encoded.body = "" + obj.body;
    return encoded;
}

function encodeSpeciality(obj: API.Speciality) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.name = "" + obj.name;
    return encoded;
}

function encodeUserDetails(obj: API.UserDetails) {
    const encoded: any = {};
    encoded.name = "" + obj.name;
    encoded.email = obj.email === null || obj.email === undefined ? null : "" + obj.email;
    encoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? encodeImageUrl(obj.avatar) : null;
    encoded.gender = typeof(obj.gender) === "string" ? obj.gender : API.Gender[obj.gender];
    return encoded;
}

function encodeDoctorType(obj: API.DoctorType) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.name = "" + obj.name;
    encoded.docName = "" + obj.docName;
    encoded.canInvitePatient = obj.canInvitePatient;
    return encoded;
}

function encodeDoctorDetails(obj: API.DoctorDetails) {
    const encoded: any = {};
    encoded.type = "" + obj.type;
    encoded.typeName = "" + obj.typeName;
    encoded.bio = "" + obj.bio;
    encoded.doc = "" + obj.doc;
    encoded.docName = "" + obj.docName;
    encoded.docPhoto = obj.docPhoto === null || obj.docPhoto === undefined ? null : obj.docPhoto ? encodeImageUrl(obj.docPhoto) : null;
    encoded.state = "" + obj.state;
    encoded.specialities = Array.isArray(obj.specialities) ? obj.specialities.map((el: API.Speciality) => el ? encodeSpeciality(el) : null) : null;
    encoded.workplaces = Array.isArray(obj.workplaces) ? obj.workplaces.map((el: string) => "" + el) : null;
    return encoded;
}

function encodeUser(obj: API.User) {
    const encoded: any = {};
    encoded.name = "" + obj.name;
    encoded.email = obj.email === null || obj.email === undefined ? null : "" + obj.email;
    encoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? encodeImageUrl(obj.avatar) : null;
    encoded.gender = typeof(obj.gender) === "string" ? obj.gender : API.Gender[obj.gender];
    encoded.id = "" + obj.id;
    encoded.phone = "" + obj.phone;
    encoded.setup_complete = obj.setup_complete;
    encoded.is_doctor = obj.is_doctor;
    encoded.doctor_details = obj.doctor_details === null || obj.doctor_details === undefined ? null : obj.doctor_details ? encodeDoctorDetails(obj.doctor_details) : null;
    encoded.is_my_patient = obj.is_my_patient;
    encoded.is_my_doctor = obj.is_my_doctor;
    encoded.updated_at = obj.updated_at ? obj.updated_at.toISOString() : null;
    return encoded;
}

function encodeSendDoctorDetailsDoc(obj: API.SendDoctorDetailsDoc) {
    const encoded: any = {};
    encoded.doc = obj.doc === null || obj.doc === undefined ? null : "" + obj.doc;
    encoded.docPhoto = obj.docPhoto === null || obj.docPhoto === undefined ? null : obj.docPhoto ? obj.docPhoto.toString("base64") : null;
    return encoded;
}

function encodeMaybeAvatar(obj: API.MaybeAvatar) {
    const encoded: any = {};
    encoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? encodeImageUrl(obj.avatar) : null;
    return encoded;
}

function encodeDoctorFilter(obj: API.DoctorFilter) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.name = "" + obj.name;
    return encoded;
}

function encodeDoctorSearchQuery(obj: API.DoctorSearchQuery) {
    const encoded: any = {};
    encoded.name = obj.name === null || obj.name === undefined ? null : "" + obj.name;
    encoded.speciality = obj.speciality === null || obj.speciality === undefined ? null : "" + obj.speciality;
    return encoded;
}

function encodeHistoryItem(obj: API.HistoryItem) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.time = obj.time ? obj.time.toISOString() : null;
    encoded.medicine = "" + obj.medicine;
    encoded.summary = "" + obj.summary;
    return encoded;
}

function encodeChat(obj: API.Chat) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.name = "" + obj.name;
    encoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? encodeImageUrl(obj.avatar) : null;
    encoded.is_group = obj.is_group;
    return encoded;
}

function encodeBaseMessage(obj: API.BaseMessage) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.updated_at = obj.updated_at ? obj.updated_at.toISOString() : null;
    encoded.time = obj.time ? obj.time.toISOString() : null;
    encoded.chat_id = "" + obj.chat_id;
    encoded.sender_id = "" + obj.sender_id;
    encoded.status = typeof(obj.status) === "string" ? obj.status : API.MessageStatus[obj.status];
    encoded.type = typeof(obj.type) === "string" ? obj.type : API.MessageType[obj.type];
    encoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    encoded.image = obj.image === null || obj.image === undefined ? null : obj.image ? encodeImageUrl(obj.image) : null;
    encoded.document = obj.document === null || obj.document === undefined ? null : obj.document ? encodeDocumentUrl(obj.document) : null;
    encoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note ? encodeAudioNoteUrl(obj.audio_note) : null;
    return encoded;
}

function encodeMessage(obj: API.Message) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.updated_at = obj.updated_at ? obj.updated_at.toISOString() : null;
    encoded.time = obj.time ? obj.time.toISOString() : null;
    encoded.chat_id = "" + obj.chat_id;
    encoded.sender_id = "" + obj.sender_id;
    encoded.status = typeof(obj.status) === "string" ? obj.status : API.MessageStatus[obj.status];
    encoded.type = typeof(obj.type) === "string" ? obj.type : API.MessageType[obj.type];
    encoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    encoded.image = obj.image === null || obj.image === undefined ? null : obj.image ? encodeImageUrl(obj.image) : null;
    encoded.document = obj.document === null || obj.document === undefined ? null : obj.document ? encodeDocumentUrl(obj.document) : null;
    encoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note ? encodeAudioNoteUrl(obj.audio_note) : null;
    encoded.user = obj.user === null || obj.user === undefined ? null : obj.user ? encodeUser(obj.user) : null;
    encoded.chat = obj.chat === null || obj.chat === undefined ? null : obj.chat ? encodeChat(obj.chat) : null;
    return encoded;
}

function encodeSimpleMessage(obj: API.SimpleMessage) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.updated_at = obj.updated_at ? obj.updated_at.toISOString() : null;
    encoded.time = obj.time ? obj.time.toISOString() : null;
    encoded.chat_id = "" + obj.chat_id;
    encoded.sender_id = "" + obj.sender_id;
    encoded.status = typeof(obj.status) === "string" ? obj.status : API.MessageStatus[obj.status];
    encoded.type = typeof(obj.type) === "string" ? obj.type : API.MessageType[obj.type];
    encoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    encoded.image = obj.image === null || obj.image === undefined ? null : obj.image ? encodeImageUrl(obj.image) : null;
    encoded.document = obj.document === null || obj.document === undefined ? null : obj.document ? encodeDocumentUrl(obj.document) : null;
    encoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note ? encodeAudioNoteUrl(obj.audio_note) : null;
    return encoded;
}

function encodeMessageToSend(obj: API.MessageToSend) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.chat_id = "" + obj.chat_id;
    encoded.type = typeof(obj.type) === "string" ? obj.type : API.MessageType[obj.type];
    encoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    encoded.image = obj.image === null || obj.image === undefined ? null : obj.image ? obj.image.toString("base64") : null;
    encoded.document = obj.document === null || obj.document === undefined ? null : obj.document ? obj.document.toString("base64") : null;
    encoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note ? obj.audio_note.toString("base64") : null;
    return encoded;
}

function encodeChatDetails(obj: API.ChatDetails) {
    const encoded: any = {};
    encoded.id = "" + obj.id;
    encoded.name = "" + obj.name;
    encoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? encodeImageUrl(obj.avatar) : null;
    encoded.is_group = obj.is_group;
    encoded.messages = Array.isArray(obj.messages) ? obj.messages.map((el: API.Message) => el ? encodeMessage(el) : null) : null;
    encoded.is_member = obj.is_member;
    encoded.unread_count = obj.unread_count;
    encoded.user = obj.user === null || obj.user === undefined ? null : obj.user ? encodeUser(obj.user) : null;
    return encoded;
}

function decodeImageUrl(obj: any) {
    const decoded: API.ImageUrl = {} as any;
    decoded.sha256 = "" + obj.sha256;
    decoded.small_url = "" + obj.small_url;
    decoded.url = "" + obj.url;
    decoded.original_width = obj.original_width;
    decoded.original_height = obj.original_height;
    decoded.nano = obj.nano !== null ? new Buffer(obj.nano.toString(), "base64") : null;
    return decoded;
}

function decodeDocumentUrl(obj: any) {
    const decoded: API.DocumentUrl = {} as any;
    decoded.sha256 = "" + obj.sha256;
    decoded.url = "" + obj.url;
    decoded.name = "" + obj.name;
    decoded.size_bytes = obj.size_bytes;
    decoded.mime_type = "" + obj.mime_type;
    return decoded;
}

function decodeAudioNoteUrl(obj: any) {
    const decoded: API.AudioNoteUrl = {} as any;
    decoded.sha256 = "" + obj.sha256;
    decoded.url = "" + obj.url;
    decoded.size_bytes = "" + obj.size_bytes;
    decoded.length_ms = obj.length_ms;
    return decoded;
}

function decodePushMessageData(obj: any) {
    const decoded: API.PushMessageData = {} as any;
    decoded.chat_id = "" + obj.chat_id;
    decoded.type = "" + obj.type;
    decoded.unread_count = obj.unread_count === null || obj.unread_count === undefined ? null : obj.unread_count;
    return decoded;
}

function decodePushMessageNotification(obj: any) {
    const decoded: API.PushMessageNotification = {} as any;
    decoded.title = "" + obj.title;
    decoded.body = "" + obj.body;
    decoded.icon = "" + obj.icon;
    decoded.color = "" + obj.color;
    decoded.badge = obj.badge === null || obj.badge === undefined ? null : obj.badge;
    return decoded;
}

function decodePushMessage(obj: any) {
    const decoded: API.PushMessage = {} as any;
    decoded.to = "" + obj.to;
    decoded.priority = "" + obj.priority;
    decoded.notification = obj.notification === null || obj.notification === undefined ? null : obj.notification ? decodePushMessageNotification(obj.notification) : null;
    decoded.data = obj.data === null || obj.data === undefined ? null : obj.data ? decodePushMessageData(obj.data) : null;
    return decoded;
}

function decodePush(obj: any) {
    const decoded: API.Push = {} as any;
    decoded.targets = obj.targets === null || obj.targets === undefined ? null : obj.targets ? obj.targets.map((el: string) => "" + el) : null;
    decoded.title = "" + obj.title;
    decoded.body = "" + obj.body;
    return decoded;
}

function decodeSpeciality(obj: any) {
    const decoded: API.Speciality = {} as any;
    decoded.id = "" + obj.id;
    decoded.name = "" + obj.name;
    return decoded;
}

function decodeUserDetails(obj: any) {
    const decoded: API.UserDetails = {} as any;
    decoded.name = "" + obj.name;
    decoded.email = obj.email === null || obj.email === undefined ? null : "" + obj.email;
    decoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? decodeImageUrl(obj.avatar) : null;
    decoded.gender = API.Gender[obj.gender as number] as any;
    return decoded;
}

function decodeDoctorType(obj: any) {
    const decoded: API.DoctorType = {} as any;
    decoded.id = "" + obj.id;
    decoded.name = "" + obj.name;
    decoded.docName = "" + obj.docName;
    decoded.canInvitePatient = obj.canInvitePatient;
    return decoded;
}

function decodeDoctorDetails(obj: any) {
    const decoded: API.DoctorDetails = {} as any;
    decoded.type = "" + obj.type;
    decoded.typeName = "" + obj.typeName;
    decoded.bio = "" + obj.bio;
    decoded.doc = "" + obj.doc;
    decoded.docName = "" + obj.docName;
    decoded.docPhoto = obj.docPhoto === null || obj.docPhoto === undefined ? null : obj.docPhoto ? decodeImageUrl(obj.docPhoto) : null;
    decoded.state = "" + obj.state;
    decoded.specialities = obj.specialities ? obj.specialities.map((el: API.Speciality) => el ? decodeSpeciality(el) : null) : null;
    decoded.workplaces = obj.workplaces ? obj.workplaces.map((el: string) => "" + el) : null;
    return decoded;
}

function decodeUser(obj: any) {
    const decoded: API.User = {} as any;
    decoded.name = "" + obj.name;
    decoded.email = obj.email === null || obj.email === undefined ? null : "" + obj.email;
    decoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? decodeImageUrl(obj.avatar) : null;
    decoded.gender = API.Gender[obj.gender as number] as any;
    decoded.id = "" + obj.id;
    decoded.phone = "" + obj.phone;
    decoded.setup_complete = obj.setup_complete;
    decoded.is_doctor = obj.is_doctor;
    decoded.doctor_details = obj.doctor_details === null || obj.doctor_details === undefined ? null : obj.doctor_details ? decodeDoctorDetails(obj.doctor_details) : null;
    decoded.is_my_patient = obj.is_my_patient;
    decoded.is_my_doctor = obj.is_my_doctor;
    decoded.updated_at = obj.updated_at !== null ? new Date(Date.parse(obj.updated_at)) : null;
    return decoded;
}

function decodeSendDoctorDetailsDoc(obj: any) {
    const decoded: API.SendDoctorDetailsDoc = {} as any;
    decoded.doc = obj.doc === null || obj.doc === undefined ? null : "" + obj.doc;
    decoded.docPhoto = obj.docPhoto === null || obj.docPhoto === undefined ? null : obj.docPhoto !== null ? new Buffer(obj.docPhoto.toString(), "base64") : null;
    return decoded;
}

function decodeMaybeAvatar(obj: any) {
    const decoded: API.MaybeAvatar = {} as any;
    decoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? decodeImageUrl(obj.avatar) : null;
    return decoded;
}

function decodeDoctorFilter(obj: any) {
    const decoded: API.DoctorFilter = {} as any;
    decoded.id = "" + obj.id;
    decoded.name = "" + obj.name;
    return decoded;
}

function decodeDoctorSearchQuery(obj: any) {
    const decoded: API.DoctorSearchQuery = {} as any;
    decoded.name = obj.name === null || obj.name === undefined ? null : "" + obj.name;
    decoded.speciality = obj.speciality === null || obj.speciality === undefined ? null : "" + obj.speciality;
    return decoded;
}

function decodeHistoryItem(obj: any) {
    const decoded: API.HistoryItem = {} as any;
    decoded.id = "" + obj.id;
    decoded.time = obj.time !== null ? new Date(Date.parse(obj.time)) : null;
    decoded.medicine = "" + obj.medicine;
    decoded.summary = "" + obj.summary;
    return decoded;
}

function decodeChat(obj: any) {
    const decoded: API.Chat = {} as any;
    decoded.id = "" + obj.id;
    decoded.name = "" + obj.name;
    decoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? decodeImageUrl(obj.avatar) : null;
    decoded.is_group = obj.is_group;
    return decoded;
}

function decodeBaseMessage(obj: any) {
    const decoded: API.BaseMessage = {} as any;
    decoded.id = "" + obj.id;
    decoded.updated_at = obj.updated_at !== null ? new Date(Date.parse(obj.updated_at)) : null;
    decoded.time = obj.time !== null ? new Date(Date.parse(obj.time)) : null;
    decoded.chat_id = "" + obj.chat_id;
    decoded.sender_id = "" + obj.sender_id;
    decoded.status = API.MessageStatus[obj.status as number] as any;
    decoded.type = API.MessageType[obj.type as number] as any;
    decoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    decoded.image = obj.image === null || obj.image === undefined ? null : obj.image ? decodeImageUrl(obj.image) : null;
    decoded.document = obj.document === null || obj.document === undefined ? null : obj.document ? decodeDocumentUrl(obj.document) : null;
    decoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note ? decodeAudioNoteUrl(obj.audio_note) : null;
    return decoded;
}

function decodeMessage(obj: any) {
    const decoded: API.Message = {} as any;
    decoded.id = "" + obj.id;
    decoded.updated_at = obj.updated_at !== null ? new Date(Date.parse(obj.updated_at)) : null;
    decoded.time = obj.time !== null ? new Date(Date.parse(obj.time)) : null;
    decoded.chat_id = "" + obj.chat_id;
    decoded.sender_id = "" + obj.sender_id;
    decoded.status = API.MessageStatus[obj.status as number] as any;
    decoded.type = API.MessageType[obj.type as number] as any;
    decoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    decoded.image = obj.image === null || obj.image === undefined ? null : obj.image ? decodeImageUrl(obj.image) : null;
    decoded.document = obj.document === null || obj.document === undefined ? null : obj.document ? decodeDocumentUrl(obj.document) : null;
    decoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note ? decodeAudioNoteUrl(obj.audio_note) : null;
    decoded.user = obj.user === null || obj.user === undefined ? null : obj.user ? decodeUser(obj.user) : null;
    decoded.chat = obj.chat === null || obj.chat === undefined ? null : obj.chat ? decodeChat(obj.chat) : null;
    return decoded;
}

function decodeSimpleMessage(obj: any) {
    const decoded: API.SimpleMessage = {} as any;
    decoded.id = "" + obj.id;
    decoded.updated_at = obj.updated_at !== null ? new Date(Date.parse(obj.updated_at)) : null;
    decoded.time = obj.time !== null ? new Date(Date.parse(obj.time)) : null;
    decoded.chat_id = "" + obj.chat_id;
    decoded.sender_id = "" + obj.sender_id;
    decoded.status = API.MessageStatus[obj.status as number] as any;
    decoded.type = API.MessageType[obj.type as number] as any;
    decoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    decoded.image = obj.image === null || obj.image === undefined ? null : obj.image ? decodeImageUrl(obj.image) : null;
    decoded.document = obj.document === null || obj.document === undefined ? null : obj.document ? decodeDocumentUrl(obj.document) : null;
    decoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note ? decodeAudioNoteUrl(obj.audio_note) : null;
    return decoded;
}

function decodeMessageToSend(obj: any) {
    const decoded: API.MessageToSend = {} as any;
    decoded.id = "" + obj.id;
    decoded.chat_id = "" + obj.chat_id;
    decoded.type = API.MessageType[obj.type as number] as any;
    decoded.text = obj.text === null || obj.text === undefined ? null : "" + obj.text;
    decoded.image = obj.image === null || obj.image === undefined ? null : obj.image !== null ? new Buffer(obj.image.toString(), "base64") : null;
    decoded.document = obj.document === null || obj.document === undefined ? null : obj.document !== null ? new Buffer(obj.document.toString(), "base64") : null;
    decoded.audio_note = obj.audio_note === null || obj.audio_note === undefined ? null : obj.audio_note !== null ? new Buffer(obj.audio_note.toString(), "base64") : null;
    return decoded;
}

function decodeChatDetails(obj: any) {
    const decoded: API.ChatDetails = {} as any;
    decoded.id = "" + obj.id;
    decoded.name = "" + obj.name;
    decoded.avatar = obj.avatar === null || obj.avatar === undefined ? null : obj.avatar ? decodeImageUrl(obj.avatar) : null;
    decoded.is_group = obj.is_group;
    decoded.messages = obj.messages ? obj.messages.map((el: API.Message) => el ? decodeMessage(el) : null) : null;
    decoded.is_member = obj.is_member;
    decoded.unread_count = obj.unread_count;
    decoded.user = obj.user === null || obj.user === undefined ? null : obj.user ? decodeUser(obj.user) : null;
    return decoded;
}

setTimeout(() => API._connect(), 0);
export default API;



// WEBPACK FOOTER //
// ./src/vendor/api.ts